// FFL Term Underwriting Grid — 5 carriers × ~70 conditions.
//
// Source: Family First Life Term Grid Sheet (provided by the FFL
// home office). Mirrors the FEX placement grid shape so the SAME
// placement UI works for both products — agents toggle conditions,
// the tool ranks carriers by worst-case verdict.
//
// CARRIERS (column order must match each row's verdicts array):
//   0  Americo            — Home Mortgage Series
//   1  Ethos              — TruStage® Term (Prime / Spectrum / Select)
//   2  Prosperity         — Family Freedom Term
//   3  Mutual of Omaha    — TLE / IULE
//   4  American-Amicable  — Term Made Simple
//
// TIERS (used for ranking):
//   ok          — accept day-one (writes here)
//   conditional — depends on timeframe / specifics. Read detail.
//   check       — route to a different product (e.g. Ethos Select)
//   decline     — hard decline
//
// The carrier with the LOWEST worst-case tier across the selected
// conditions wins. Always confirm against the carrier's e-app.

export const TERM_CARRIERS = [
  { name: 'Americo',           product: 'Home Mortgage Series' },
  { name: 'Ethos',             product: 'TruStage® Term' },
  { name: 'Prosperity',        product: 'Family Freedom Term' },
  { name: 'Mutual of Omaha',   product: 'TLE / IULE' },
  { name: 'American-Amicable', product: 'Term Made Simple' },
];

export const TERM_TIERS = {
  ok:          { r: 1,   tone: 'emerald', label: 'Accept' },
  conditional: { r: 2,   tone: 'amber',   label: 'Conditional' },
  check:       { r: 2.5, tone: 'slate',   label: 'Route/Check' },
  decline:     { r: 5,   tone: 'red',     label: 'Decline' },
};

// Compact constructors.
const D = (detail) => ({ tier: 'decline',     detail });
const C = (detail) => ({ tier: 'conditional', detail });
const O = (detail) => ({ tier: 'ok',          detail });
const K = (detail) => ({ tier: 'check',       detail });
const r = (name, ame, eth, pro, moo, ama) => ({ name, verdicts: [ame, eth, pro, moo, ama] });

export const TERM_CONDITIONS = [
  r('AIDS / HIV / ARC',
    C('Diagnosed within 10 yrs - DECLINE'), D('Ineligible'), D('DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Alcohol / Drug Abuse',
    C('Within 10 yrs - DECLINE (check med list)'), C('Within 10 yrs routed to Select; <5 yrs to Ethos FE'), C('Within 5 yrs - DECLINE'), C('Treatment within last 10 yrs - DECLINE'), C('Within 4 yrs - DECLINE')),
  r("ALS (Lou Gehrig's)",
    D('Ever had - DECLINE'), D('Ineligible'), C('Ever diagnosed or treatment <5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r("Alzheimer's / Dementia",
    D('Ever had - DECLINE'), D('Ineligible'), C('Ever diagnosed or treatment <5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Amputation',
    D('Ever had - DECLINE'), C('OK if fully recovered + due to trauma/injury; else rated for cause'), O('Not asked'), K('See Diabetic Complications'), C('Caused by injury - OK; caused by disease - DECLINE')),
  r('Aneurysm',
    D('Ever had - DECLINE'), D('Ineligible'), C('Within 5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Angina (Chest Pain)',
    D('DECLINE'), C('Within 10 yrs route to Select; <5 yrs to Ethos FE'), K('Not asked - see meds'), K('Not asked - see meds'), D('DECLINE')),
  r('Angioplasty',
    D('Ever had - DECLINE'), D('Ineligible'), C('Within 5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Anxiety',
    C('Mild/situational <6 mos or hospitalized - DECLINE'), C('Mild/controlled OK; severe <10 yrs to Select; <2 yrs to FE'), K('Not asked - see meds'), O('Not asked'), C('1 med + situational - OK; major depression - DECLINE')),
  r('Arthritis',
    C('Rheumatoid/debilitating - DECLINE; infusions or oral steroids - DECLINE; hospitalized <12 mos - DECLINE'), C('RA impacting work >5d/mo or under 30 - route to Select'), C('Within 5 yrs - DECLINE'), C('Moderate/severe RA on med - DECLINE'), C('RA minimal - OK; otherwise DECLINE')),
  r('AFib / Arrhythmia',
    D('Ever had - DECLINE'), C('Ineligible if <2 yrs or treated surgically (other than ablation)'), C('Within 5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Assisted Living / Long-Term Care',
    D('DECLINE'), D('Ineligible'), C('Within 12 mos - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Asthma (Chronic)',
    C('Chronic - DECLINE; daily oral meds - DECLINE'), C('Chronic severe ineligible; chronic moderate to Select'), C('Acute OK; any other chronic lung - DECLINE'), C('Severe/chronic - DECLINE; asthma+tobacco - DECLINE'), K('OK - check meds')),
  r('Autoimmune Disorder',
    D('DECLINE'), C('Rate for specific dx; non-specific - ineligible'), C('Within 5 yrs - DECLINE'), O('Not asked'), O('Not asked')),
  r('Autism',
    D('DECLINE'), C('Mild OK; severe to Select'), D('DECLINE'), K('See mental incapacity'), K('See mental incapacity')),
  r('Bipolar',
    D('Ever had or been treated - DECLINE'), C('Ineligible if dx <2 yrs or inpatient <5 yrs'), D('DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Black Lung',
    D('Ever had - DECLINE'), D('Ineligible'), K('Not asked - see treatment'), K('Not asked - see treatment'), K('Not asked - see treatment')),
  r('Blood Clots',
    D('DECLINE'), D('Chronic clotting disorders ineligible'), K('See meds / Stent'), K('See meds / Stent'), K('See meds / Stent')),
  r('Blood Disorder (Clotting)',
    D('DECLINE'), D('Chronic clotting disorders ineligible'), K('Not asked - check for stents'), K('Not asked - check for stents'), K('Not asked - check for stents')),
  r('Bone Disease / Disorder',
    C('Surgery <12 mos w/o doc release - DECLINE'), C('Ineligible if chronic + assistive devices'), K('Not asked - check meds'), K('Not asked - check meds'), K('Not asked - check meds')),
  r('Bone Marrow Transplant',
    D('DECLINE'), D('Ineligible'), D('DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Bronchitis',
    D('Ever had - DECLINE'), C('Acute OK; chronic ineligible'), C('Chronic <5 yrs - DECLINE'), C('Severe/chronic - DECLINE'), C('Acute+recovered OK; chronic - DECLINE')),
  r('Cancer (other than Basal Cell)',
    D('Ever had - DECLINE'), C('Basal/squamous skin OK; otherwise ineligible'), C('Within 5 yrs - DECLINE (except basal/squamous)'), C('Basal/squamous OK; otherwise DECLINE'), C('Basal/squamous OK; >8 yrs no recurrence OK; else DECLINE')),
  r('Cardiomyopathy',
    D('Ever had - DECLINE'), D('Ineligible'), C('Within 5 yrs - DECLINE'), D('Ever had - DECLINE'), D('DECLINE')),
  r('Cellulitis',
    D('DECLINE'), C('OK if treated + fully recovered'), O('Not asked'), O('Not asked'), O('Not asked')),
  r('Cerebral Palsy',
    D('DECLINE'), D('Ineligible'), O('Not asked'), O('Not asked'), D('DECLINE')),
  r('Chronic Pain (narcotic Rx)',
    C('Narcotic for chronic condition >6 mos - DECLINE'), C('Multi-narcotic 3-6 mos - route to Select or ineligible'), K('Not asked - check meds'), K('Not asked - check meds'), K('Not asked - see treatment')),
  r('Cirrhosis',
    D('DECLINE'), D('Ineligible'), C('Within 5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Congestive Heart Failure',
    D('Ever had - DECLINE'), D('Ineligible'), C('Within 5 yrs - DECLINE'), D('Ever had - DECLINE'), D('DECLINE')),
  r('Coronary Artery Disease',
    D('Ever had - DECLINE'), D('Ineligible'), C('Within 5 yrs - DECLINE'), D('Ever had - DECLINE'), D('DECLINE')),
  r('COPD',
    D('Ever had - DECLINE'), D('Ineligible'), C('Within 5 yrs - DECLINE'), D('Ever had - DECLINE'), D('DECLINE')),
  r("Crohn's Disease",
    D('DECLINE'), C('Ineligible if dx or surgery <2 yrs'), O('Not asked'), O('Not asked'), C('Dx <12 mos or before age 10 - DECLINE')),
  r('Cystic Fibrosis',
    D('Ever had - DECLINE'), D('Ineligible'), C('Within 5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Defibrillator Implant / Pacemaker',
    D('Ever had - DECLINE'), D('Ineligible'), D('DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Dementia',
    D('Ever had - DECLINE'), D('Ineligible'), C('Ever diagnosed or treatment <5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Depression',
    C('Major/chronic or bipolar - DECLINE; mild <6 mos or hospitalized - DECLINE'), C('Ineligible if hospitalization/ER <5 yrs or disability claim'), C('Bipolar - DECLINE; otherwise see meds'), C('Bipolar - DECLINE; otherwise not asked'), C('Bipolar - DECLINE; otherwise not asked')),
  r('Disabled / on Disability',
    C('Last 12 mos - case by case'), C('To Select depending on dx; else ineligible'), C('Within 12 mos - DECLINE'), C('Back/neck/hips OK; other - case by case'), C('SSE or unemployed due to medical - DECLINE')),
  r('Dialysis',
    D('Ever had - DECLINE'), D('Ineligible'), C('Within 12 mos - DECLINE; advised - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Disc Disease',
    C('Chronic joint or disc disease - DECLINE'), C('Surgery pending or not recovered - to Select'), O('Not asked'), O('Not asked'), O('Not asked')),
  r('Diabetes',
    C('Dx <6 mos - DECLINE; before age 35 - DECLINE; insulin - DECLINE; 2+ oral meds - DECLINE'), C('Non-insulin pre-age 31 + controlled - to Select; insulin - ineligible'), C('Insulin - DECLINE; before age 50 - DECLINE; any complications - DECLINE'), C('Dx before age 50 - DECLINE; any complications - DECLINE'), C('Oral meds OK; before age 35 - DECLINE; insulin - DECLINE; combined w/ overweight/gout - DECLINE')),
  r('Diabetic Complications',
    C('Cellulitis/neuropathy/amputation - DECLINE'), D('Ineligible'), D('Any complications - DECLINE'), D('Any complications - DECLINE'), C('Combined w/ overweight/gout/retinopathy/proteinuria - DECLINE')),
  r("Down's Syndrome",
    D('DECLINE'), D('Ineligible'), D('DECLINE'), D('DECLINE'), D('DECLINE')),
  r('DUI',
    C('Within 2 yrs - DECLINE; 3+ moving violations <2 yrs - DECLINE'), C('Single DUI >5 yrs OK for Prime; <5 yrs or multiple - ineligible'), C('Conviction for DUI/DWI <2 yrs - DECLINE'), C('Within 5 yrs - DECLINE; 4+ moving violations <5 yrs - DECLINE'), C('Within 3 yrs - DECLINE; 2+ moving - DECLINE; revoked - DECLINE')),
  r('Drug Use or Treatment',
    C('Within 10 yrs - DECLINE'), C('Substance abuse <5 yrs or multi-relapse - ineligible; 6-10 yrs no relapse - Select'), C('Within 5 yrs - DECLINE'), C('Within 10 yrs - DECLINE'), C('Within 4 yrs - DECLINE; >4 yrs no use - OK')),
  r('Emphysema',
    D('Ever had - DECLINE'), D('Ineligible'), C('Within 5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Epilepsy / Seizures',
    C('Dx <6 mos, hospitalized <12 mos, or driving restrictions - DECLINE'), C('Within 6 mos to Select or ineligible based on frequency'), O('Not asked'), O('Not asked'), C('Petit Mal - OK; all others - DECLINE')),
  r('Felony',
    C('Within 10 yrs - DECLINE'), C('Convicted, incarcerated, or on parole <10 yrs - ineligible'), C('Within 5 yrs - DECLINE'), C('Within 10 yrs - DECLINE'), C('Misdemeanor/felony <5 yrs - DECLINE')),
  r('Fibromyalgia',
    O('Not asked'), C('Within 2 yrs - to Select'), O('Not asked'), O('Not asked'), O('Not asked')),
  r('Gout',
    O('Not asked'), O('Accepted'), O('Not asked'), O('Not asked'), C('Combined w/ diabetes/kidney stones/proteinuria - DECLINE')),
  r('Heart Attack',
    D('Ever had - DECLINE'), D('Ineligible'), C('Within 5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Heart Disease / Disorder',
    D('DECLINE'), C('Rate or route to different product'), C('Within 5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Heart Surgery',
    D('Ever had - DECLINE'), C('Ineligible except for ablation'), C('Within 5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Heart Valve Replacement',
    D('Ever had - DECLINE'), D('Ineligible'), C('Within 5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Hepatitis A',
    C('If fully recovered - ACCEPTED'), C('Fully recovered - ACCEPTED; not recovered - ineligible'), O('Not asked'), O('Not asked'), O('Not asked')),
  r('Hepatitis B',
    D('DECLINE'), C('Recovered <5 yrs or not recovered - ineligible'), C('Within 5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Hepatitis C',
    D('DECLINE'), C('Dx <1 yr ineligible; recovered <5 yrs to Select; >5 yrs accepted'), C('Within 5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('High Blood Pressure',
    C('Dx <4 mos - DECLINE; before age 30 - DECLINE; 3+ meds - DECLINE; abnormal EKG <12 mos - DECLINE'), C('Hospitalized/ER <3 yrs or 4+ meds - to Select'), C('If hospitalized - DECLINE; see meds'), C('Hospitalized <10 yrs - DECLINE; otherwise OK - check meds'), C('Controlled w/ <=2 meds OK; 3+ uncontrolled - DECLINE')),
  r("Hodgkin's Disease",
    D('DECLINE'), C('Ineligible if <10 yrs'), O('Not asked'), D('DECLINE'), D('DECLINE')),
  r('Hospice',
    D('DECLINE'), D('Ineligible'), C('Within 12 mos - DECLINE'), D('DECLINE'), D('DECLINE')),
  r("Huntington's Disease",
    D('DECLINE'), D('Ineligible'), C('Ever dx or treatment <5 yrs - DECLINE'), D('DECLINE'), O('Not asked')),
  r('Illegal Drugs',
    C('Within 10 yrs - DECLINE'), C('Use <5 yrs (other than marijuana) ineligible; 6-10 yrs to Select'), C('Within 5 yrs - DECLINE'), C('Within 10 yrs - DECLINE'), C('Use or treatment within 4 yrs - DECLINE')),
  r('Jail / Incarcerated',
    C('Within 10 yrs - DECLINE'), C('Incarcerated or on parole <10 yrs - ineligible'), K('Not asked; see Felony/DUI'), C('Within 10 yrs - DECLINE'), C('Misdemeanor/felony <5 yrs - DECLINE; parole <6 mos - DECLINE')),
  r('Kidney Disease / Failure',
    D('DECLINE'), D('Ineligible: failure/transplant/dialysis/unresolved'), C('Chronic <5 yrs - DECLINE'), D('DECLINE'), C('Dialysis/failure/nephrectomy/PKD/transplant - DECLINE')),
  r('Liver Disease',
    D('DECLINE'), D('Ineligible'), C('Within 5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Lupus',
    C('Systemic - DECLINE'), C('Route to Ethos Select'), C('Within 5 yrs - DECLINE'), D('DECLINE'), C('Systemic (SLE) - DECLINE')),
  r('Marijuana Use',
    C('Recreational within 24 mos - DECLINE'), C('Recreational/medicinal OK; high freq or medicinal route to Select'), C('Within 5 yrs - DECLINE'), O('Not asked'), O('Not asked')),
  r('Melanoma',
    D('DECLINE'), C('<5 yrs or >1 instance to Select; metastasis ineligible'), K('See Cancer'), K('See Cancer'), K('See Cancer')),
  r('Mental Incapacity / Retardation',
    D('DECLINE'), D('Ineligible'), C('Ever dx or treatment <5 yrs - DECLINE'), D('DECLINE'), C('Mild/moderate retardation - OK; severe - DECLINE')),
  r('Military',
    C('Active duty OK; currently deployed - DECLINE'), C('Deployed/planned/hazmat to Select or ineligible'), O('Not asked'), O('Not asked'), O('Not asked')),
  r('Multiple Sclerosis (MS)',
    D('DECLINE'), C('<5 yrs + assistive to Select; progressive/IV infusion/BMT ineligible'), C('Ever dx or treatment <5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Muscular Dystrophy',
    D('DECLINE'), D('Ineligible'), C('Ever dx or treatment <5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Muscle Disease / Disorder',
    C('Surgery <12 mos w/o doc release - DECLINE'), C('Ineligible if chronic/acute w/o recovery'), O('Not asked'), O('Not asked'), O('Not asked')),
  r('Neuropathy',
    C('Combined w/ diabetes - DECLINE'), C('Diabetic - route to Select; chronic ineligible'), C('Combined w/ diabetes - DECLINE'), C('Combined w/ diabetes - DECLINE'), K('See Diabetes')),
  r('Oxygen',
    D('DECLINE'), D('Ineligible'), C('Used or advised within 12 mos - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Pacemaker / Defibrillator Implant',
    D('DECLINE'), D('Ineligible'), D('DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Pain Medication',
    C('Narcotic chronic >6 mos - DECLINE'), C('Multi-narcotic 3-6 mos - to Select or ineligible'), K('Check med list'), K('Not asked - check med list'), K('Not asked - check med list')),
  r('Pancreatitis',
    O('Not asked'), C('Single >5 yrs non-alcohol + recovered - ACCEPTED; 2 episodes - to Select; multi or <5 yrs ineligible'), O('Not asked'), C('Chronic or alcohol-related - DECLINE'), C('Chronic or multiple episodes - DECLINE')),
  r('Paraplegia',
    D('DECLINE'), D('Ineligible'), C('Ever dx or treatment <5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r("Parkinson's Disease",
    D('DECLINE'), C('Route to Ethos Select'), C('Ever dx or treatment <5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Parole / Probation (currently)',
    C('Within 10 yrs - DECLINE'), D('Ineligible'), C('Currently on or awaiting trial - DECLINE'), C('Within 10 yrs - DECLINE'), C('Misdemeanor/felony <5 yrs - DECLINE; parole <6 mos - DECLINE')),
  r('PAD / PVD',
    O('Not asked'), D('Ineligible'), C('Combined w/ diabetes - DECLINE'), C('Combined w/ diabetes - DECLINE'), D('DECLINE')),
  r('Pilot / Student Pilot',
    C('Within 2 yrs - DECLINE'), C('Student/recreational/non-major-carrier - ineligible'), O('Not asked'), O('Not asked'), C('Personal pilot <100 hrs - DECLINE; student - DECLINE')),
  r('Pregnant',
    C('Gestational diabetes, HBP, multiple fetus, bed rest - DECLINE'), C('Ineligible if currently pregnant w/ complications'), O('Not asked'), C('Not asked - but ask about gestational'), C('Current w/ no complications - OK')),
  r('PTSD',
    O('Not asked'), C('<6 mos, >3 meds, or disability - to Select; inpatient <2 yrs or suicide <5 yrs ineligible'), K('Not asked - check med list'), O('Not asked'), K('Not asked - check meds')),
  r('Pulmonary Fibrosis',
    D('Ever had - DECLINE'), D('Ineligible'), O('Not asked'), D('DECLINE'), D('DECLINE')),
  r('Quadriplegia',
    D('DECLINE'), D('Ineligible'), C('Ever dx or treatment <5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Renal Disease',
    C('End-stage or renal insufficiency - DECLINE'), C('Renal failure - ineligible (see Kidney)'), C('End-stage <5 yrs - DECLINE; see dialysis'), C('End stage w/ dialysis - DECLINE'), D('DECLINE')),
  r('Restless Leg Syndrome',
    K('See if meds treat Parkinson\'s - potential decline'), C('If not related to Parkinson\'s + no treatment pending - ACCEPTED'), K('See if meds treat Parkinson\'s - potential decline'), K('See if meds treat Parkinson\'s'), K('See if meds treat Parkinson\'s')),
  r('Rheumatoid Arthritis',
    K('See Arthritis'), C('RA impacting work >5d/mo or under 30 - to Select'), C('Within 5 yrs - DECLINE'), K('See Arthritis'), C('Minimal or slight impairment - OK; all others - DECLINE')),
  r('Sarcoidosis',
    D('Ever had - DECLINE'), C('Route to Ethos Select'), K('Not asked - check meds'), D('DECLINE'), C('Pulmonary - DECLINE')),
  r('Schizophrenia',
    D('Ever had or treated - DECLINE'), D('Ineligible'), D('DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Seizures',
    C('Dx <6 mos, hospitalized <12 mos, or driving restrictions - DECLINE'), C('Within 6 mos - to Select or ineligible'), O('Not asked'), O('Not asked'), C('Petit Mal - OK; all others - DECLINE')),
  r('Sickle Cell Anemia',
    D('DECLINE'), D('Ineligible'), D('DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Sleep Apnea',
    C('Dx <6 mos - DECLINE; CPAP/BiPAP - OK; w/o CPAP/BiPAP - DECLINE'), C('Irregular CPAP or combined w/ overweight/arrhythmia - to Select; non-compliant or w/ stroke/CAD/HTN/COPD - ineligible'), O('Not asked'), O('Not asked'), C('Combined w/ overweight, HBP, COPD, or arrhythmia - DECLINE')),
  r('Smoker / Nicotine Use',
    C('Cigars/pipes/chew/snuff/gum/patch/vape/e-cig <2 yrs - SMOKER RATE'), C('Any tobacco/nicotine other than cigars, or >12 cigars/yr - tobacco rates'), C('Any tobacco or nicotine replacement - SMOKER RATE'), C('Any tobacco or nicotine replacement - SMOKER RATE'), C('Cigarettes/ecigs/chew/pipes/snuff/patch/gum/inhaler <12 mos - SMOKER RATE; excludes occasional cigar')),
  r('Spina Bifida',
    O('Not asked'), C('Surgical + recovered - OK; surgical w/o recovery - to Select; assistive devices/persistent symptoms - ineligible'), O('Not asked'), O('Not asked'), C('Asymptomatic - OK; otherwise DECLINE')),
  r('Stent',
    D('Ever had - DECLINE'), D('Ineligible'), C('Within 5 yrs - DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Stroke / TIA',
    D('DECLINE'), C('Within 5 yrs - to Select or ineligible based on episodes'), C('Within 5 yrs - DECLINE'), D('DECLINE'), C('TIA after 6 mos OK; otherwise DECLINE')),
  r('Terminal Illness',
    D('DECLINE'), D('Ineligible'), D('DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Transplant',
    D('DECLINE'), D('Ineligible'), D('DECLINE'), D('DECLINE'), D('DECLINE')),
  r('Tuberculosis',
    O('Not asked'), C('Current + not recovered - ineligible; recovered w/ residuals - to Select'), O('Not asked'), O('Not asked'), C('Over 2 yrs - OK; within 2 yrs of treatment - DECLINE')),
  r('Tumor',
    C('Any in brain - DECLINE'), C('Any in brain, or <6 mos - ineligible'), C('Within 5 yrs - DECLINE'), K('Not asked (check treatment)'), K('Not asked (check treatment)')),
  r('Ulcerative Colitis',
    D('DECLINE'), C('<6 mos + weight loss >9lbs + symptoms - to Select or ineligible'), O('Not asked'), O('Not asked'), C('Dx before 20 or within 12 mos - DECLINE')),
  r('Wheelchair / Electric Scooter',
    D('DECLINE'), C('Due to chronic illness/disease - see specific condition'), C('Within 12 mos - DECLINE'), D('DECLINE'), O('Not asked')),
];

// Rank carriers by their WORST verdict across the agent's selected
// conditions. Lower = better. Ties broken alphabetically.
export function rankTermCarriers(indices) {
  if (!indices || indices.length === 0) return null;
  const stats = TERM_CARRIERS.map((c, ci) => {
    let worstR = 0;
    let limiting = '';
    const details = [];
    for (const i of indices) {
      const v = TERM_CONDITIONS[i].verdicts[ci];
      const tier = TERM_TIERS[v.tier];
      if (tier && tier.r > worstR) {
        worstR = tier.r;
        limiting = TERM_CONDITIONS[i].name;
      }
      details.push({ condition: TERM_CONDITIONS[i].name, ...v });
    }
    return { ci, name: c.name, product: c.product, worstR, limitingCondition: limiting, details };
  });
  return stats.sort((a, b) => a.worstR - b.worstR || a.name.localeCompare(b.name));
}
